import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import { crush } from '@tedconf/js-crushinator-helpers';
import classNames from 'classnames';
import PrismicStyledText from 'components/PRISMIC/PrismicStyledText';
import { PageLayoutContext } from 'components/pages/prismic/Page/PageContent';
import { useContext, type JSX } from 'react';
import PrismicConditionalLink from '../../PrismicConditionalLink';
import PrismicInlineText from '../../PrismicInlineText';
import { PrismicNextImage } from '../../PrismicNextImage';
import styles from '../../scss/pages.module.scss';
import utils, { Alignment } from '../../utils';

/**
 * Props for `ImageWithWrappedText`.
 */
export type ImageWithWrappedTextProps =
  SliceComponentProps<Content.ImageWithWrappedTextSlice>;

/**
 * Component for "ImageWithWrappedText" Slices.
 */
const ImageWithWrappedText = ({
  slice
}: ImageWithWrappedTextProps): JSX.Element => {
  const hasAlignment = slice.primary.image_alignment !== 'None';
  const widthStyle = hasAlignment ? { maxWidth: '30%' } : {};
  const columnWidth: number = useContext(PageLayoutContext);
  const isFloating: boolean = columnWidth > 0.2 && hasAlignment;
  const crushinatorWidth = Math.min(Math.floor(50 * columnWidth) * 10, 100);

  const image = (
    <figure
      className={classNames(
        styles.Figure,
        isFloating &&
          slice.primary.wrap_text_around_image &&
          (slice.primary.image_alignment === 'Left'
            ? styles.Figure__left
            : slice.primary.image_alignment === 'Right'
              ? styles.Figure__right
              : '')
      )}
      style={slice.primary.wrap_text_around_image ? widthStyle : {}}
    >
      <PrismicConditionalLink field={slice.primary.link}>
        {slice.variation !== 'legacy' ? (
          <PrismicNextImage
            field={slice.primary.image}
            className={styles.Figure__image}
          />
        ) : (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={crush(slice.primary.image_url || '', {
              width: crushinatorWidth
            })}
            alt={slice.primary.image_alt || ''}
            className={styles.Figure__image}
          />
        )}
      </PrismicConditionalLink>
      {isFilled.richText(slice.primary.image_caption) && (
        <figcaption className={styles.Figure__caption}>
          <PrismicInlineText field={slice.primary.image_caption} />
        </figcaption>
      )}
    </figure>
  );

  const text = (
    <div
      className={
        slice.primary.image_alignment === 'None'
          ? utils.getAlignClass(Alignment[slice.primary.text_alignment])
          : 'text-left'
      }
    >
      <PrismicStyledText renderParagraphTags field={slice.primary.text} />
    </div>
  );

  if (slice.primary.wrap_text_around_image || !isFloating) {
    return (
      <div
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="clearfix"
      >
        {image}
        {text}
      </div>
    );
  }

  return (
    <div
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={styles.media}
    >
      <div
        className={classNames(
          styles.media__image,
          slice.primary.image_alignment === 'Right' &&
            styles['media__image--alt']
        )}
        style={widthStyle}
      >
        {image}
      </div>
      <div className={styles.media__message}>{text}</div>
    </div>
  );
};

export default ImageWithWrappedText;
